import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { updateUserProfile } from '../store/slices/authSlice';
import { sendProfileUpdateNotification } from '../emailjs/emailService';
import { HiOutlineX, HiOutlineCheck, HiOutlineExclamation, HiOutlineCamera, HiOutlineTrash } from 'react-icons/hi';
import './ProfilePictureModal.scss';

const ProfilePictureModal = ({ isOpen, onClose, userData, onUpdateStart, onUpdateComplete }) => {
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(userData.img || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  // Add function to handle modal overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      
      setSelectedFile(file);
      setError('');
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    // Notify parent component that update is starting
    if (onUpdateStart) {
      onUpdateStart();
    }

    try {
      let imageUrl = userData.img; // Keep existing image if no new file selected

      if (selectedFile) {
        // In a real application, you would upload the file to a storage service
        // For now, we'll use the data URL as the image URL
        // In production, you should upload to Firebase Storage, AWS S3, etc.
        imageUrl = previewUrl;
      }

      const updatedData = {
        img: imageUrl
      };

      await dispatch(updateUserProfile({
        email: userData.email,
        profileData: updatedData
      })).unwrap();

      // Send profile update notification email
      try {
        await sendProfileUpdateNotification(
          userData,
          'profile_picture',
          { img: 'Profile picture updated' }
        );
        console.log('Profile picture update notification sent successfully');
      } catch (emailError) {
        console.error('Failed to send profile picture update notification:', emailError);
      }

      setSuccess('Profile picture updated successfully!');
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error updating profile picture:', err);
      setError(err.message || 'Failed to update profile picture. Please try again.');
    } finally {
      setIsSubmitting(false);
      // Notify parent component that update is complete
      if (onUpdateComplete) {
        onUpdateComplete();
      }
    }
  };

  return (
    <div className="profile-picture-modal">
      <div className="modal-overlay" onClick={handleOverlayClick}></div>
      <div className="modal-container">
        <div className="modal-header">
          <h2>Update Profile Picture</h2>
          <button className="close-button" onClick={onClose}>
            <HiOutlineX />
          </button>
        </div>

        {error && (
          <div className="alert error">
            <HiOutlineExclamation />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="alert success">
            <HiOutlineCheck />
            <span>{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="profile-picture-form">
          <div className="image-preview-section">
            <div className="image-preview">
              {previewUrl ? (
                <img src={previewUrl} alt="Profile preview" />
              ) : (
                <div className="no-image-placeholder">
                  <HiOutlineCamera />
                  <span>No image selected</span>
                </div>
              )}
            </div>
            
            <div className="image-actions">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*"
                style={{ display: 'none' }}
              />
              
              <button
                type="button"
                className="select-image-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSubmitting}
              >
                <HiOutlineCamera />
                {previewUrl ? 'Change Image' : 'Select Image'}
              </button>
              
              {previewUrl && (
                <button
                  type="button"
                  className="remove-image-button"
                  onClick={handleRemoveImage}
                  disabled={isSubmitting}
                >
                  <HiOutlineTrash />
                  Remove
                </button>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-button" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="save-button"
              disabled={isSubmitting || (!selectedFile && !previewUrl)}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfilePictureModal;
